import {
  Component,
  OnInit,
  Inject
} from '@angular/core';

import {
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';

import {
  FormGroup,
  FormBuilder,
  Validators
} from '@angular/forms';

import {
  Observable,
  of,
  Subscription
} from 'rxjs';

import {
  debounceTime,
  distinctUntilChanged,
  tap,
  switchMap,
  catchError
} from 'rxjs/operators';

import {
  QuestionnaireService
} from '@shared/services/questionnaire.service';

import {
  SharedDataService
} from '@shared/services/shared-data.service';

import { AnswerSet } from '@shared/models/answerset';
import { ClientSite } from '@app/shared/models/client';
import { UserSubscriber } from '@app/shared/subscribers/user.subscriber';
import { User } from '@app/shared/models/user';

@Component({
  selector: 'db-answer-set-dialog',
  templateUrl: './answer-set-dialog.component.html',
  styleUrls: ['./answer-set-dialog.component.scss']
})
export class AnswerSetDialogComponent implements OnInit {
  answerSetForm: FormGroup;

  dataset: any;
  paramsDataset: any;

  questionnaireFieldReadWrite: boolean;
  clientIdFieldReadWrite: boolean;
  clientSiteFieldReadWrite: boolean;

  // Dropdown settings
  sectorDropdownList = [];
  sectorDropdownSettings = {};

  clientSiteDropdownList = [];
  clientSiteDropdownSettings = {};

  questionnaireDropdownList = [];
  questionnaireDropdownSettings = {};

  selectedClientId: string;
  selectedClientSiteId: string;
  selectedQuestionnaireId: string;

  searching = false;
  searchFailed = false;

  user: User;
  isSiteFeatureEnabled: boolean;

  subscription: Subscription = new Subscription();

  resultFormatter = (value: any) => value.can || '';

  searchClient = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      tap(() => this.searching = true),
      switchMap(term =>
        this.questionnaireService.getClients(term).pipe(
          tap(() => {
            this.searchFailed = false;
          }),
          catchError(() => {
            this.searchFailed = true;
            return of([]);
          }))
      ),
      tap(() => this.searching = false)
    )

  constructor(
    private questionnaireService: QuestionnaireService,
    private sharedDataService: SharedDataService,
    private formBuilder: FormBuilder,
    private answerSetDialogRef: MatDialogRef<AnswerSetDialogComponent>,
    private userSubscriber: UserSubscriber,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit() {
    this.subscription.add(this.subscribeToUser());

    this.paramsDataset = this.data.params ? this.data.params.data : null;
    this.clientIdFieldReadWrite = true;

    this.initializeSelectDropdowns();

    if (this.paramsDataset) {
      this.buildAnswerSetForm(
        new AnswerSet(
          this.paramsDataset.questionnaireId ? this.paramsDataset.id : '',
          this.paramsDataset.answerSetName,
          this.paramsDataset.client ? this.paramsDataset.clientId : this.data.clientId,
          this.paramsDataset.clientSite ? this.paramsDataset.clientSite.siteID : '',
          this.paramsDataset.questionnaireId ? this.paramsDataset.questionnaireId : this.paramsDataset.id,
          null,
        )
      );
    } else {
      this.buildAnswerSetForm(
        new AnswerSet('','', this.data.clientId, '', '', null)
      );

      this.setDataAndReadWriteStates();
    }
  }

  private subscribeToUser(): Subscription {
    return this.userSubscriber.user$.subscribe({
      next: (user: User) => {
        this.user = user;
        this.isSiteFeatureEnabled = this.user.isSiteFeatureEnabled(user.selectedDepartment);
      },
    });
  }

  setDataAndReadWriteStates() {
    this.questionnaireFieldReadWrite = true;
    this.getQuestionnaires();

    if (this.data.clientId) {
      this.clientIdFieldReadWrite = false;
      this.getClientById();
    }
  }

  initializeSelectDropdowns() {
    this.sectorDropdownSettings = {
      closeDropDownOnSelection: false,
      idField: 'id',
      itemsShowLimit: 3,
      singleSelection: false,
      textField: 'sectorName'
    };

    this.clientSiteDropdownSettings = {
      idField: 'id',
      textField: 'siteName',
      noDataAvailablePlaceholderText: 'Please select a CAN to display sites'
    };

    this.questionnaireDropdownSettings = {
      idField: 'id',
      textField: 'questionnaireName'
    };

    this.getSectors();
  }

  getSectors() {
    this.questionnaireService.getSectors()
      .subscribe(result => {
        this.sectorDropdownList = result;
      });
  }

  getClientById() {
    this.questionnaireService.getClientSitesByClientId(this.data.clientId)
      .subscribe({
        next: (result: ClientSite[]) => {
          this.clientSiteDropdownList = result;
        },
        error: (error) => {
          console.error('Error loading client sites:', error);
          this.clientSiteDropdownList = [];
        }
      });
  }

  getQuestionnaires() {
    this.questionnaireService.getQuestionnaires()
      .subscribe(result => {
        this.questionnaireDropdownList = result;
      });
  }

  buildAnswerSetForm(data: any) {
    const clientSiteValidator = this.isSiteFeatureEnabled ? Validators.required : Validators.nullValidator;
    this.answerSetForm = this.formBuilder.group({
      id: [
        data.id,
        Validators.nullValidator
      ],
      answerSetName: [
        data.answerSetName,
        Validators.required
      ],
      clientId: [
        data.clientId,
        Validators.required
      ],
      clientSiteId: [
        data.clientSiteId,
        clientSiteValidator
      ],
      questionnaireId: [
        data.questionnaireId,
        Validators.required
      ],
      sectors: [
        data.sectors,
        Validators.required
      ]
    });
  }

  selectedClient(client) {
    this.selectedClientId = client.item.clientId;

    this.questionnaireService.getClientSitesByClientId(this.selectedClientId)
      .subscribe(clientSites => {
        this.clientSiteDropdownList = clientSites;
      });
  }

  selectedSite(site: ClientSite) {
    this.selectedClientSiteId = site.id;
  }

  selectedQuestionnaire(questionnaire) {
    this.selectedQuestionnaireId = questionnaire.id;
  }

  patchFormValues() {
    this.answerSetForm.patchValue({
      clientId: this.selectedClientId,
      clientSiteId: this.isSiteFeatureEnabled ? this.selectedClientSiteId : '',
    });

    if (this.data.clientId) {
      this.answerSetForm.patchValue({
        questionnaireId: this.selectedQuestionnaireId
      });
    }
  }

  submitAnswerSetForm() {
    this.patchFormValues();

    const clientId = this.answerSetForm.get('clientId').value;

    const answerSetFormValue = JSON.stringify(this.answerSetForm.value);

    this.answerSetDialogRef.close(([answerSetFormValue, clientId]));
  }

  closeDialog() {
    this.answerSetDialogRef.close();
  }
}
